import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Cliente } from '../../core/models/cliente';
import { ClienteService } from 'src/app/core/services/cliente.service';
import { Sexo } from 'src/app/core/enums/sexo';
import { TipoPessoa } from 'src/app/core/enums/tipoPessoa';
import { MessageService, ConfirmationService } from 'primeng/api';
import { AuthService } from '../../core/services/auth.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-cliente',
  templateUrl: './cliente.component.html',
  styleUrls: ['./cliente.component.scss'],
})
export class ClienteComponent implements OnInit, OnDestroy {
  cliente = new Cliente();
  clientes: Cliente[] = [];

  // edição
  editMode = false;
  editClienteId: number | null = null;

  @ViewChild('clienteForm') clienteForm?: NgForm;

  private destroy$ = new Subject<void>();
  sexoOptions = Object.values(Sexo);
  tipoPessoaOptions = Object.values(TipoPessoa);

  constructor(
    private readonly servico: ClienteService,
    private readonly messageService: MessageService,
    private readonly confirmation: ConfirmationService,
    private readonly authService: AuthService
  ) {}

  selecionar(): void {
    this.servico.selecionar().subscribe((retorno) => (this.clientes = retorno));
  }

  cadastrar(): void {
    // Validações básicas no front-end antes de enviar
    let invalid = false;

const idValue = (this.cliente.cpfCnpj || '').toString();

// remove apenas máscara
const doc = idValue.replace(/[^a-zA-Z0-9]/g, '');

if (doc) {

  if (this.isFisica) {

    if (doc.length !== 11) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'CPF deve ter 11 caracteres.'
      });
      invalid = true;
    }

  } else {

    if (doc.length !== 14) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'CNPJ deve ter 14 caracteres.'
      });
      invalid = true;
    }

  }

}

    this.servico.cadastrar(this.cliente).subscribe({
      next: (retorno) => {
        this.clientes.push(retorno);
        this.cliente = new Cliente();
        // garante pessoa física por padrão após cadastro
        this.cliente.tipoPessoa = 'FISICA' as any;
        // atualiza o template-driven form com o novo modelo (marca o radio)
        try {
          this.clienteForm?.resetForm(this.cliente);
        } catch (e) {}
        this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Cliente cadastrado com sucesso.' });
      },
      error: (err) => {
        // O ErrorInterceptor já formata o erro para conter { status, statusText, message, body }
        // Mas o backend pode retornar message como string ou dentro de body
        let userMessage = 'Erro ao cadastrar cliente';

        if (!err) {
          userMessage = 'Erro desconhecido';
        } else if (typeof err === 'string') {
          userMessage = err;
        } else if (err.message) {
          userMessage = err.message;
        } else if (err.body) {
          if (typeof err.body === 'string') userMessage = err.body;
          else if (err.body.message) userMessage = err.body.message;
          else userMessage = JSON.stringify(err.body);
        }

        this.messageService.add({ severity: 'error', summary: 'Erro', detail: userMessage });
        // Mantém log técnico para debug
        console.error('Erro ao cadastrar cliente:', err);
      }
    });
  }

  editar(c: Cliente): void {
    this.editMode = true;
    this.editClienteId = c.id ?? null;
    this.cliente = { ...c } as Cliente;
  }

  cancelarEdicao(): void {
    this.editMode = false;
    this.editClienteId = null;
    this.cliente = new Cliente();
    // garantir pessoa física após cancelar
    this.cliente.tipoPessoa = 'FISICA' as any;
    // reset template-driven form if present
    try {
      this.clienteForm?.resetForm(this.cliente);
    } catch (e) {
      // ignore if form not available
    }
  }

  limparFormulario(): void {
    // clear model and form controls
    this.cliente = new Cliente();
    // garantir pessoa física ao limpar
    this.cliente.tipoPessoa = 'FISICA' as any;
    this.editMode = false;
    this.editClienteId = null;
    try {
      this.clienteForm?.resetForm(this.cliente);
    } catch (e) {}
  }

  salvarAlteracao(): void {
    if (!this.editMode || this.editClienteId == null) return;
    this.servico.alterar(this.editClienteId, this.cliente).subscribe({
      next: (ret) => {
        const idx = this.clientes.findIndex((x) => x.id === this.editClienteId);
        if (idx >= 0) this.clientes[idx] = ret;
        this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Cliente alterado com sucesso.' });
        this.cancelarEdicao();
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao alterar cliente.' });
        console.error('Erro ao alterar cliente:', err);
      },
    });
  }

  confirmarAtivarDesativar(cliente: Cliente | undefined): void {
    if (!cliente?.id) return;
    const willDeactivate = !!cliente.ativo;
    const actionLabel = willDeactivate ? 'desativar' : 'ativar';

    this.confirmation.confirm({
      message: `Confirma ${actionLabel} o cliente ${cliente.nome || cliente.id}?`,
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: () => {
        this.servico.ativarDesativar(cliente.id as number, !willDeactivate).subscribe({
          next: (ret) => {
            const idx = this.clientes.findIndex((u) => u.id === cliente.id);
            if (idx >= 0) {
              if (ret) this.clientes[idx] = ret;
              else this.clientes[idx].ativo = !willDeactivate;
            }
            this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: `Cliente ${willDeactivate ? 'desativado' : 'ativado'} com sucesso.` });
          },
          error: (err) => {
            const msg = err?.error?.message || err?.message || `Erro ao ${actionLabel} cliente`;
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: msg });
            console.error(`Erro ao ${actionLabel} cliente:`, err);
          },
        });
      },
    });
  }

  // Chamado quando o usuário troca o tipo de pessoa (FISICA / JURIDICA)
  onTipoPessoaChange(): void {
    // Limpa o campo CPF/CNPJ para evitar conflito entre formatos
    this.cliente.cpfCnpj = '';
  }

  // Conveniências para template
  get isFisica(): boolean {
    return this.cliente?.tipoPessoa === 'FISICA';
  }

  get idMask(): string {
    return this.isFisica ? '000.000.000-00' : '00.000.000/0000-00';
  }

  get idLabel(): string {
    return this.isFisica ? 'CPF' : 'CNPJ';
  }

  get idPlaceholder(): string {
    return this.isFisica ? 'CPF' : 'CNPJ';
  }

  // Método de inicialização
  ngOnInit(): void {
    this.authService.currentTenant$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.selecionar();
      });

    this.selecionar();

    // Define tipoPessoa padrão para Pessoa Física
    if (!this.cliente.tipoPessoa) {
      this.cliente.tipoPessoa = 'FISICA' as any;
    }

    this.sexoOptions = Object.values(Sexo);
    this.tipoPessoaOptions = Object.values(TipoPessoa);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
